import React, { useState, useEffect } from "react";
import { MapPin, Phone, Mail, ArrowRight, Clock, CheckCircle2, Sparkles } from "lucide-react";
import { submitContact } from "../lib/api";
import { useLang } from "../context/I18nContext";
import { toast } from "sonner";

const PROJECT_TYPES = ["Wohnung", "Einfamilienhaus", "Mehrfamilienhaus", "Wohnanlage", "Gewerbe", "Neubau", "Sanierung"];
const SCOPES = ["Planung", "BIM", "Wärmepumpe", "Heizung", "Kühlung", "Warmwasser", "PV", "Energie", "Automation", "Licht", "Beschattung", "Zutritt", "Service"];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", location: "", message: "",
    projectType: "", scopes: [],
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [prefill, setPrefill] = useState(null);

  // Pre-fill from Produktfinder / Energie-Rechner (sessionStorage)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("eco_prefill");
      if (!raw) return;
      const data = JSON.parse(raw);
      setPrefill(data);
      // Build prefilled message
      const parts = [];
      if (data.source === "produktfinder") {
        parts.push("Produktfinder-Ergebnis:");
        if (data.systems?.length) parts.push("Empfohlene Systeme: " + data.systems.join(", "));
        if (data.answers) parts.push("Antworten: " + Object.entries(data.answers).map(([k, v]) => `${k}=${v}`).join(", "));
      } else if (data.source === "energie-rechner") {
        parts.push("Energie-Rechner-Ergebnis:");
        const b = data.building; const r = data.result;
        if (b) parts.push(`Standort: ${b.country || b.region}, Klimazone ${b.microClimate || "Standard"}`);
        if (b) parts.push(`Gebäude: ${b.standard || b.type}, ${b.area} m², ${b.occupants || "–"} Personen, Heizflächen ${b.emitter || "–"}`);
        if (b) parts.push(`Bestandsenergie: ${b.source}, Stromtarif ${b.tariff || "–"} EUR/kWh`);
        if (b) parts.push(`PV: ${b.roofArea} m² Dach, Ausrichtung ${b.orientation}`);
        if (r) parts.push(`Heizlast ca. ${r.heatLoad} kW, Empfehlung ${r.wpTarget}, SCOP ca. ${r.scop}`);
        if (r) parts.push(`Jahreswärme ca. ${r.annualKwh} kWh, Wärmepumpenstrom ca. ${r.wpElectricity} kWh`);
        if (r) parts.push(`PV-Potenzial ${r.kwpPossible} kWp, Jahresertrag ${r.annualYield} kWh`);
        if (r) parts.push(`Mögliche Ersparnis: € ${r.saving} (Heizung) + € ${r.annualSavingPV} (PV) /Jahr`);
        setForm((f) => ({ ...f, scopes: Array.from(new Set([...f.scopes, "Wärmepumpe", "Energie", "PV"])) }));
      }
      setForm((f) => ({ ...f, message: parts.join("\n") }));
      // Clear so we don't double-apply on reload
      sessionStorage.removeItem("eco_prefill");
    } catch {}
  }, []);

  const toggleScope = (s) => {
    setForm((f) => ({
      ...f,
      scopes: f.scopes.includes(s) ? f.scopes.filter((x) => x !== s) : [...f.scopes, s],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const extraMessage = [
        form.projectType ? `Projektart: ${form.projectType}` : null,
        form.scopes.length ? `Leistungsbereiche: ${form.scopes.join(", ")}` : null,
        form.location ? `Projektort: ${form.location}` : null,
        form.company ? `Firma: ${form.company}` : null,
        form.message,
      ].filter(Boolean).join("\n");

      await submitContact({ name: form.name, email: form.email, phone: form.phone, message: extraMessage });
      setSent(true);
      setForm({ name: "", email: "", phone: "", company: "", location: "", message: "", projectType: "", scopes: [] });
      toast.success(t("contact.toastOk"));
    } catch {
      toast.error(t("contact.toastErr"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page-shell" data-testid="contact-page">
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">{t("contact.eyebrow")}</div>
        <h1 className="page-title">
          {t("contact.h1.1")}<br/>{t("contact.h1.2")} <span className="italic-accent">{t("contact.h1.italic")}</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          {t("contact.intro")}
        </p>
      </section>

      <section className="site-container section-block">
        <div className="grid grid-cols-1 lg:grid-cols-12 surface-panel">
          {/* Direct contact */}
          <div className="lg:col-span-4 p-7 md:p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0A1F38]/55">
            <div className="eyebrow mb-8 text-[#5FA637]">{t("contact.direct")}</div>
            <div className="space-y-10">
              <InfoBlock
                icon={MapPin}
                title={t("contact.addressLbl")}
                body={<>ECO BUILDING TECHNIK GMBH<br/>Seepromenade 109<br/>AT-2384 Ebreichsdorf<br/>Österreich</>}
              />
              <InfoBlock icon={Phone} title={t("contact.mobileLbl")} body={<a href="tel:+436643289599" className="hover:text-[#5FA637] transition-colors">+43 664 328 95 99</a>}/>
              <InfoBlock icon={Mail} title={t("contact.emailLbl")} body={<a href="mailto:office@eco-building.tech" className="hover:text-[#5FA637] transition-colors">office@eco-building.tech</a>}/>
              <InfoBlock icon={Clock} title={t("contact.hoursLbl")} body={<>{t("contact.hours1")}<br/>{t("contact.hours2")}</>}/>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8 p-7 md:p-10 lg:p-12">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20" data-testid="contact-success">
                <div className="w-16 h-16 border border-[#5FA637] text-[#5FA637] flex items-center justify-center mb-7">
                  <CheckCircle2 size={22}/>
                </div>
                <h3 className="font-display text-3xl mb-3">{t("contact.successH1")} <span className="italic-accent">{t("contact.successIt")}</span></h3>
                <p className="text-white/65 text-sm max-w-sm">{t("contact.successText")}</p>
                <button onClick={() => setSent(false)} className="btn-ghost mt-10" data-testid="contact-new">{t("contact.newMsg")}</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="eyebrow mb-2 text-[#5FA637]">{t("contact.formTitle")}</div>

                {prefill && (
                  <div className="flex items-start gap-3 p-3 border border-[#5FA637]/40 bg-[#5FA637]/5 text-xs text-white/80" data-testid="prefill-notice">
                    <Sparkles size={14} className="text-[#5FA637] mt-0.5 shrink-0"/>
                    <div>
                      <div className="font-semibold text-[#5FA637] uppercase tracking-wider mb-1">
                        {prefill.source === "produktfinder" ? "Produktfinder-Ergebnis" : "Energie-Rechner-Daten"} übernommen
                      </div>
                      Nachricht ist vorbefüllt — Sie können ergänzen oder bearbeiten.
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input required placeholder={t("contact.namePh")} className="input-field"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name"/>
                  <input placeholder={t("contact.companyPh")} className="input-field"
                    value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company"/>
                  <input required type="email" placeholder={t("contact.emailPh")} className="input-field"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email"/>
                  <input placeholder={t("contact.phonePh")} className="input-field"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone"/>
                  <input placeholder={t("contact.locationPh")} className="input-field sm:col-span-2"
                    value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="contact-location"/>
                </div>

                {/* Project type */}
                <div className="pt-4">
                  <div className="eyebrow mb-3">{t("contact.projectType")}</div>
                  <div className="flex flex-wrap gap-2" data-testid="contact-projecttype">
                    {PROJECT_TYPES.map((p) => (
                      <button
                        key={p} type="button"
                        onClick={() => setForm({ ...form, projectType: p })}
                        data-testid={`contact-projecttype-${p.toLowerCase().replaceAll(" ", "-")}`}
                        className={`btn-pill ${form.projectType === p ? "active" : ""}`}
                      >{p}</button>
                    ))}
                  </div>
                </div>

                {/* Scopes */}
                <div className="pt-4">
                  <div className="eyebrow mb-3">{t("contact.scope")}</div>
                  <div className="flex flex-wrap gap-2" data-testid="contact-scopes">
                    {SCOPES.map((s) => (
                      <button
                        key={s} type="button" onClick={() => toggleScope(s)}
                        data-testid={`contact-scope-${s.toLowerCase().replaceAll(" ", "-")}`}
                        className={`btn-pill ${form.scopes.includes(s) ? "active" : ""}`}
                      >{s}</button>
                    ))}
                  </div>
                </div>

                <textarea required rows={5} placeholder={t("contact.messagePh")} className="input-field resize-none"
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message"/>

                <div className="pt-6">
                  <button type="submit" disabled={sending} className="btn-primary disabled:opacity-60" data-testid="contact-submit">
                    {sending ? t("contact.sending") : <>{t("contact.submit")} <ArrowRight size={14}/></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoBlock({ icon: Icon, title, body }) {
  return (
    <div className="flex gap-5">
      <div className="w-11 h-11 border border-[#5FA637]/40 text-[#5FA637] flex items-center justify-center shrink-0">
        <Icon size={15} strokeWidth={1.6}/>
      </div>
      <div>
        <div className="eyebrow mb-2">{title}</div>
        <div className="text-sm text-white/85 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
